// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as Sentry from '@sentry/react';
import { handleAPIError } from '@/api/error-utils';
import { downloadFileByKey } from '@/file/utilities/download-file-by-key';
import { useMutationWithGlobalErrorHandler } from '@/hooks/use-mutation-with-global-error-handler';
import { isAxiosError } from 'axios';

async function downloadByKey(fileKey: string): Promise<void> {
  try {
    await downloadFileByKey(fileKey);
  } catch (error) {
    // Reported here rather than from onError: the global handler treats a
    // non-throwing onError as "handled locally" and would then skip the
    // user-facing message.
    const apiError = isAxiosError(error) ? handleAPIError(error) : undefined;
    if (!apiError?.sentToSentry) {
      Sentry.captureException(error);
    }
    throw error;
  }
}

/**
 * Downloads a stored file by its key through an authenticated request and saves it
 * directly, instead of opening it in a new tab. Exposes pending state so callers
 * can show a spinner while the blob is fetched.
 */
export function useDownloadFileByKey() {
  return useMutationWithGlobalErrorHandler({
    mutationFn: downloadByKey,
    errorMessage: 'Could not download that file. Please try again.',
  });
}
